<template>
    <Modal class="split-payment-modal"
           size="sm"
           icon="fa-hexagon-divide"
           title="SPLIT_PAYMENT.MODAL_TITLE"
           :cancel="cancel">
        <div class="form-v">
            <div class="form-group">
                <label class="control-label">
                    {{ l('SPLIT_PAYMENT.AMOUNT_LABEL') }}
                </label>
                <div class="btn-group-flex">
                    <button class="btn btn-default btn-lg btn--flex"
                            :class="{active: selectedTotal === 'order'}"
                            @click.prevent="selectedTotal = 'order'">
                        <span>
                            <strong>{{ l('SPLIT_PAYMENT.AMOUNT__ORDER') }}</strong>
                            <br /><small>{{ currencyFilter(orderTotal) }}</small>
                        </span>
                    </button>
                    <button class="btn btn-default btn-lg btn--flex"
                            :class="{active: selectedTotal === 'remaining'}"
                            @click.prevent="selectedTotal = 'remaining'">
                        <span>
                            <strong>{{ l('SPLIT_PAYMENT.AMOUNT__REMAINING') }}</strong>
                            <br /><small>{{ currencyFilter(remainingBalance) }}</small>
                        </span>
                    </button>
                </div>
            </div>

            <div class="form-group">
                <label class="control-label">
                    {{ l('SPLIT_PAYMENT.NUMBER_LABEL') }}
                </label>
                <div class="split-payment-btns">
                    <button v-for="button in buttons"
                            :key="button.label"
                            class="btn btn-info btn-lg"
                            :disabled="button.value > maxPayment"
                            @click.prevent="ok(button.value)">
                        <span>
                            <strong>{{ button.number }} {{ l('SPLIT_PAYMENT.PEOPLE') }}</strong>
                            <br /><small>{{ currencyFilter(button.value) }}</small>
                        </span>
                    </button>
                    <button class="btn btn-info btn-lg"
                            @click.prevent="customSplit()">
                        <span>
                            {{ l('SPLIT_PAYMENT.NUMBER__OTHER') }}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script>
import BigNumber from 'bignumber.js';
import Modal from '@/vue/components/Modals/Modal';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';

export default {
    components: { Modal },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        orderTotal: {
            type: Number,
            required: true,
        },

        remainingBalance: {
            type: Number,
            required: true,
        },

        maxPayment: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            /**
             * The amount being split can either be the 'order' total or the 'remaining' balance.
             */
            selectedTotal: 'order',
        };
    },

    computed: {
        amountToSplit() {
            return this.selectedTotal === 'order' ? this.orderTotal : this.remainingBalance;
        },

        /**
         * Create an array of all the buttons to be shown.
         * For each of the 'splits' defined it will generate two buttons -
         * one for splitting the order total that much, and one for splitting the remainig balance that much.
         *
         * @return {unknown[]}
         */
        buttons() {
            let buttons = [];

            for (let number = 2; number <= 6; number++) {
                buttons.push({
                    number,
                    value: new BigNumber(this.amountToSplit)
                        .dividedBy(number)
                        .decimalPlaces(2)
                        .toNumber(),
                });
            }

            return buttons;
        },
    },

    methods: {
        customSplit() {
            container.dialogService
                .numPad(
                    null,
                    {
                        title: 'SPLIT_PAYMENT.NUMBER_LABEL',
                        min: 2,
                        initialValue: 0,
                        numPadOptions: {
                            showBackspaceButton: false,
                            showClearButton: false,
                            showSignButton: false,
                            showDecimalButton: false,
                        },
                    }
                )
                .then(({ value }) => {
                    const amount = new BigNumber(this.amountToSplit)
                        .dividedBy(value)
                        .decimalPlaces(2)
                        .toNumber();
                    this.ok(amount);
                })
                .catch(suppressModalCancelledErrorOrReThrow);
        },

        ok(value) {
            this.resolve({ value });
        },
    },
};
</script>

<style lang="less">
@import "../../../../less/variables";

.split-payment-btns {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-gap: @half-gutter-width;
}
</style>
